import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { ThemeProvider } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { createAppTheme, dashboardModeCssVariables } from '@repo/ui/theme'
import { VulcanAppProvider } from '@repo/vulcan-tools'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import type { VulcanClientRegistry } from '../../clients/createClientRegistry'
import { resolveEmbeddedApp } from '../../configuration/appRegistry'
import { appForPath, VULCAN_APP_CONFIG, type VulcanAppDefinition } from '../../configuration/apps'
import { VulcanNavigation } from '../navigation/VulcanNavigation'
import { AppViewport, ShellContent } from './Vulcan.styles'

export function Vulcan({ clients }: { clients: VulcanClientRegistry }) {
  const auth = useAuth()
  const [path, setPath] = useState(() => window.location.pathname)
  const [darkMode, setDarkMode] = useState(false)
  const activeApp = appForPath(path)
  const mode = darkMode ? 'dark' : 'light'
  const toggleColorMode = useCallback(() => setDarkMode((value) => !value), [])
  const theme = useMemo(() => createAppTheme(mode), [mode])
  const themeVariables = useMemo(() => dashboardModeCssVariables(mode), [mode])

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const navigate = useCallback((app: VulcanAppDefinition) => {
    window.history.pushState({}, '', app.path)
    setPath(app.path)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [])

  if (!activeApp) {
    return <Alert severity="error">Vulcan has no configured applications.</Alert>
  }

  const EmbeddedApp = resolveEmbeddedApp(activeApp)
  return (
    <ThemeProvider theme={theme}>
      <Box
        data-dashboard-theme={mode}
        style={themeVariables}
        sx={{ display: 'flex', height: '100dvh', overflow: 'hidden', bgcolor: 'dashboard.background.default' }}
      >
        <VulcanNavigation
          apps={VULCAN_APP_CONFIG}
          activeAppId={activeApp.id}
          mode={mode}
          email={auth.user?.profile.email}
          onNavigate={navigate}
          onToggleColorMode={toggleColorMode}
          onSignOut={() => auth.signoutRedirect()}
        />
        <ShellContent>
          <AppViewport>
            <Suspense fallback={<Typography sx={{ p: 3 }}>Loading {activeApp.label}…</Typography>}>
              <VulcanAppProvider
                value={{
                  app: activeApp,
                  clients,
                  colorMode: mode,
                  toggleColorMode
                }}
              >
                <EmbeddedApp />
              </VulcanAppProvider>
            </Suspense>
          </AppViewport>
        </ShellContent>
      </Box>
    </ThemeProvider>
  )
}
